/**
 * TicketsView - the Tickets tab: every ticket sent with "Submit a ticket"
 * (and the ones imported from Sharif's inbox), to work through. The 'tickets'
 * area (src/data/roles.js): View reads, Edit changes status, priority,
 * assignee and the resolution note. Admin's alone by default.
 *
 * The list opens on Open + In progress; a row opens the ticket beside it
 * (views/tickets/TicketDetail). Deep link: #/tickets?ticket=<id>.
 * Live: a new ticket (kind 'feedback') or a change (kind 'tickets') re-reads.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { canEdit } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { parseHashFilters } from '../utils/hashParams';
import { latestReads, useLiveRefresh } from '../live/useLive';
import TicketDetail from './tickets/TicketDetail';
import { errorOf, listTickets } from './tickets/ticketsApi';
import { DEFAULT_STATUSES, KIND_LABELS, PRIORITY_LABELS, STATUSES, STATUS_LABELS, ageOf, pageOf, searchRows } from './tickets/ticketsRules';

const KIND_STYLE = { bug: 'bg-red-50 text-red-700 border-red-200', idea: 'bg-blue-50 text-blue-700 border-blue-200' };
const STATUS_STYLE = {
  open: 'bg-amber-50 text-amber-800 border-amber-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  done: 'bg-green-50 text-green-800 border-green-200',
  wont_do: 'bg-gray-100 text-gray-600 border-gray-200',
};
const chip = 'inline-block px-1.5 py-0.5 rounded border text-[10.5px] font-bold whitespace-nowrap';
const th = 'text-left px-3 py-2 text-[11px] font-semibold text-text-muted uppercase tracking-wide';
const td = 'px-3 py-2 align-top';

export default function TicketsView() {
  const access = useMyAccess();
  const editable = canEdit(access, 'tickets');
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [kind, setKind] = useState('');
  const [from, setFrom] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ rows: [], counts: {}, senders: [] });
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(() => parseHashFilters().ticket || null);
  const [reads] = useState(latestReads);

  const load = useCallback(async () => {
    const ticket = reads.start();
    const r = await listTickets({ status: statuses.length ? statuses.join(',') : 'all', kind, from });
    if (!reads.show(ticket)) return;
    setLoading(false);
    if (!r.ok) return setErr(errorOf(r, 'Could not load the tickets.'));
    setErr(null);
    setData(r.body);
    return undefined;
  }, [statuses, kind, from, reads]);

  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh(['tickets', 'feedback'], load);

  const rows = useMemo(() => searchRows(data.rows, q), [data.rows, q]);
  const toggle = (s) => setStatuses((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <div className="flex gap-4 items-start">
      <div className="flex-1 min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <div className="flex gap-1" role="group" aria-label="Show statuses">
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={statuses.includes(s)}
                onClick={() => toggle(s)}
                className={clsx(
                  'px-2.5 py-1 rounded border font-semibold',
                  statuses.includes(s) ? 'bg-gat-green text-white border-gat-green' : 'bg-surface border-border-strong text-text-body'
                )}
              >
                {STATUS_LABELS[s]} <span className="opacity-75">{(data.counts || {})[s] ?? 0}</span>
              </button>
            ))}
          </div>
          <select
            aria-label="Kind"
            className="border border-border-strong rounded px-2 py-1 bg-surface"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="">Bugs and ideas</option>
            <option value="bug">Bugs</option>
            <option value="idea">Ideas</option>
          </select>
          <select
            aria-label="From"
            className="border border-border-strong rounded px-2 py-1 bg-surface"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          >
            <option value="">Anyone</option>
            {(data.senders || []).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            aria-label="Search tickets"
            placeholder="Search number, subject, sender, page"
            className="border border-border-strong rounded px-2.5 py-1 bg-surface flex-1 min-w-[180px]"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {err && <div className="text-status-lost text-[12px]">{err}</div>}
        <div className="bg-surface border border-border-strong rounded overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-surface-2">
              <tr>
                <th className={th}>#</th>
                <th className={th}>Kind</th>
                <th className={th}>Subject</th>
                <th className={th}>From</th>
                <th className={th}>Page</th>
                <th className={th}>Status</th>
                <th className={th}>Priority</th>
                <th className={th}>Age</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setOpen(t.id)}
                  className={clsx(
                    'border-t border-border cursor-pointer hover:bg-surface-2',
                    String(open) === String(t.id) && 'bg-gat-green/5'
                  )}
                >
                  <td className={`${td} font-semibold whitespace-nowrap`}>{t.number}</td>
                  <td className={td}>
                    <span className={clsx(chip, KIND_STYLE[t.kind])}>{KIND_LABELS[t.kind] || t.kind}</span>
                  </td>
                  <td className={td}>
                    {t.subject || '(no subject)'}
                    {t.file_count > 0 && (
                      <span className="ml-1.5 text-text-muted">
                        ({t.file_count} file{t.file_count === 1 ? '' : 's'})
                      </span>
                    )}
                  </td>
                  <td className={td}>{t.from_name || t.from_email || '-'}</td>
                  <td className={td}>{pageOf(t.page).label || '-'}</td>
                  <td className={td}>
                    <span className={clsx(chip, STATUS_STYLE[t.status])}>{STATUS_LABELS[t.status] || t.status}</span>
                  </td>
                  <td className={clsx(td, t.priority === 'high' && 'text-status-lost font-semibold')}>
                    {PRIORITY_LABELS[t.priority] || t.priority}
                  </td>
                  <td className={`${td} text-text-muted whitespace-nowrap`} title={new Date(t.created_at).toLocaleString()}>
                    {ageOf(t.created_at)}
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-text-muted">
                    {loading ? 'Loading...' : 'No tickets here.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <aside className="w-[440px] max-w-[45%] shrink-0 bg-surface border border-border-strong rounded sticky top-0 max-h-[calc(100vh-140px)] overflow-y-auto">
          <TicketDetail id={open} editable={editable} onClose={() => setOpen(null)} onSaved={load} />
        </aside>
      )}
    </div>
  );
}
